// EditConfigLabel — inline label editor for one system_config row (PATCH /api/config/:id).
// Only the label is editable; the key stays fixed because other data may reference it. This is how an
// admin changes values that live in the label, such as the M-Pesa paybill, support phone and STK flag.
import { useState } from "react";
import type { ConfigCategory, SystemConfig } from "@dm-lms/shared/types";
import { patchJson } from "../../hooks/useApi.js";
import { invalidateConfig } from "../../hooks/useConfig.js";

const MAX_LABEL_LENGTH = 100;   // matches updateConfigSchema

interface EditConfigLabelProps {
  id:       string;
  label:    string;
  category: ConfigCategory;
  hint?:    string | undefined;                 // what a valid value looks like, shown while editing
  onSaved:  (id: string, label: string) => void;
}

export default function EditConfigLabel({ id, label, category, hint, onSaved }: EditConfigLabelProps) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(label);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function open() {
    setValue(label);
    setError(null);
    setEditing(true);
  }

  function cancel() {
    setEditing(false);
    setError(null);
  }

  async function save() {
    const trimmed = value.trim();
    if (!trimmed) {
      setError("Value is required");
      return;
    }
    if (trimmed === label) {
      cancel();
      return;
    }

    setIsSaving(true);
    setError(null);
    try {
      const res = await patchJson<SystemConfig>(`/api/config/${id}`, { label: trimmed });
      if (!res.ok) {
        setError(res.error);
        return;
      }
      invalidateConfig(category);
      onSaved(id, res.data.label);
      setEditing(false);
    } catch {
      setError("Unable to save. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }

  if (!editing) {
    return (
      <button
        type="button"
        aria-label={`Edit ${label}`}
        onClick={open}
        className="rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-dm-slate transition hover:bg-gray-50"
      >
        Edit
      </button>
    );
  }

  return (
    <form
      className="max-w-xs"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <input
        type="text"
        value={value}
        maxLength={MAX_LABEL_LENGTH}
        aria-label="New value"
        autoFocus
        onChange={(event) => { setValue(event.target.value); }}
        className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm text-dm-navy focus:border-dm-royal focus:outline-none focus:ring-1 focus:ring-dm-royal"
      />
      {hint && <p className="mt-1 text-xs text-dm-slate">{hint}</p>}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      <div className="mt-2 flex gap-2">
        <button
          type="submit"
          disabled={isSaving}
          className="rounded-md bg-dm-navy px-3 py-1 text-xs font-medium text-white transition hover:bg-dm-blue disabled:opacity-50"
        >
          {isSaving ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          disabled={isSaving}
          onClick={cancel}
          className="rounded-md border border-gray-300 bg-white px-3 py-1 text-xs font-medium text-dm-slate transition hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
